import { HalloweenArt } from "../theme/HalloweenArt";
import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../reducer";
import { isAdminLike, isStaff } from "../utils/access";
import wordmark from "../assets/images/woxa-cafe-wordmark.png";
import { Channel, isChannelOpen } from "../utils/schedule";
import { useSidebarBadges } from "../hook/useSidebarBadges";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { logoutSession } from "../api/session";
import { UserAvatar } from "../avatar/UserAvatar";
import { resetMyAvatarStore, useMyAvatar } from "../avatar/useMyAvatar";

type BadgeKind = "count" | "dot";
type NavItem = {
  to: string;
  label: string;
  icon: React.ReactNode;
  channel?: Channel;
  badge?: { kind: BadgeKind; value: number };
  /** Full-page link outside the SPA router (e.g. the API-served auth monitor). */
  external?: boolean;
};

const NavBadge: React.FC<{ badge: NonNullable<NavItem["badge"]> }> = ({ badge }) => {
  if (badge.value <= 0) return null;
  if (badge.kind === "dot") {
    return (
      <span
        className="w-2 h-2 rounded-full bg-rose-500 shrink-0"
        title={`${badge.value} ready`}
        aria-label={`${badge.value} ready`}
      />
    );
  }
  const text = badge.value > 99 ? "99+" : String(badge.value);
  return (
    <span
      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold leading-none shrink-0"
      aria-label={`${badge.value} new`}
    >
      {text}
    </span>
  );
};

// ----- icon set (24x24, currentColor) -----
const I = {
  menu: (<path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/>),
  cup: (<path strokeLinecap="round" strokeLinejoin="round" d="M4 8h13a3 3 0 0 1 0 6h-1M6 14h10v3a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V8z"/>),
  bar: (<path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18l-7 9v5h3v2H7v-2h3v-5L3 4z"/>),
  clock: (<><circle cx="12" cy="12" r="9"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3"/></>),
  history: (<path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 1 0 3-6.7L3 8m0-5v5h5"/>),
  star: (<path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.5a.6.6 0 0 1 1.04 0l2.6 4.69 5.18 1a.6.6 0 0 1 .33 1.01l-3.7 3.85.69 5.21a.6.6 0 0 1-.85.62L12 17.5l-4.77 2.37a.6.6 0 0 1-.85-.62l.69-5.21-3.7-3.85a.6.6 0 0 1 .33-1.01l5.18-1L11.48 3.5z"/>),
  list: (<path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7"/>),
  grid: (<path strokeLinecap="round" strokeLinejoin="round" d="M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 0h7v7h-7v-7z"/>),
  chart: (<path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18M8 17V9m4 8V5m4 12v-6"/>),
  users: (<path strokeLinecap="round" strokeLinejoin="round" d="M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0zM2 21a8 8 0 0 1 16 0M22 21a6 6 0 0 0-9-5.2"/>),
  palette: (<path strokeLinecap="round" strokeLinejoin="round" d="M12 3a9 9 0 1 0 0 18c1.66 0 3-1.34 3-3 0-.78-.32-1.5-.81-2.05-.49-.55-.81-1.27-.81-2.05 0-1.66 1.34-3 3-3h2.62a3 3 0 0 0 3-3A8 8 0 0 0 12 3zM7 12.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5-3a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 3a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>),
  settings: (<><path strokeLinecap="round" strokeLinejoin="round" d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/><path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>),
  logout: (<path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>),
  lock: (<path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25z"/>),
  close: (<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>),
  face: (<><circle cx="12" cy="12" r="9"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 10h.01M15 10h.01M8.5 14.5a4.5 4.5 0 0 0 7 0"/></>),
  flag: (<path strokeLinecap="round" strokeLinejoin="round" d="M4 21V4M4 4h13l-2 4 2 4H4"/>),
};

const Icon = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <svg className={`w-5 h-5 ${className}`} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    {children}
  </svg>
);

// ----- nav config per role group -----
export const userNav: NavItem[] = [
  { to: "/menuCafe",    label: "Order Cafe",    icon: <Icon>{I.cup}</Icon>,    channel: "cafe" },
  { to: "/menuDessert", label: "Order Dessert", icon: <Icon>{I.cup}</Icon>,    channel: "dessert" },
  { to: "/statusOrder", label: "My Queue",      icon: <Icon>{I.clock}</Icon> },
  { to: "/ordered",     label: "History",       icon: <Icon>{I.history}</Icon> },
  { to: "/game",        label: "มินิเกม",         icon: <Icon>{I.grid}</Icon> },
];
const baristaNav: NavItem[] = [
  { to: "/ordering",               label: "Incoming Orders",    icon: <Icon>{I.list}</Icon> },
  { to: "/history",                label: "Order History",      icon: <Icon>{I.history}</Icon> },
  { to: "/menuManagement",         label: "Menu Management",    icon: <Icon>{I.grid}</Icon> },
  { to: "/dessertManagement",      label: "Dessert Management", icon: <Icon>{I.grid}</Icon> },
  { to: "/dashboard-cafe",         label: "Cafe Dashboard",     icon: <Icon>{I.chart}</Icon> },
  { to: "/dashboard-stats",        label: "Stats",              icon: <Icon>{I.chart}</Icon> },
  { to: "/broadcasts-admin",       label: "Broadcasts",         icon: <Icon>{I.flag}</Icon> },
  { to: "/friday-special-admin",   label: "Friday Special",     icon: <Icon>{I.flag}</Icon> },
];
const adminNav: NavItem[] = [
  { to: "/dashboard-admin",        label: "Admin Dashboard",    icon: <Icon>{I.chart}</Icon> },
  { to: "/dashboard-stats-admin",  label: "Stats (Admin)",      icon: <Icon>{I.chart}</Icon> },
  { to: "/admin/settings",         label: "App Settings",       icon: <Icon>{I.settings}</Icon> },
  { to: "/admin/audit-log",        label: "Audit Log",          icon: <Icon>{I.history}</Icon> },
  { to: "/userManagement",         label: "User Management",    icon: <Icon>{I.users}</Icon> },
];
// Dev-only tools. The auth monitor is served by the API (not the SPA), so it's
// an external full-page link under /api/* (the cafe nginx proxies /api → API).
const devNav: NavItem[] = [
  { to: "/api/auth/monitor",       label: "Auth Monitor",       icon: <Icon>{I.lock}</Icon>, external: true },
];

const ROLE_LABEL: Record<string, string> = {
  user: "Member",
  barista: "Barista",
  admin: "Admin",
  dev: "Developer",
};

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Section: React.FC<{ label: string; items: NavItem[]; activePath: string; onLink: () => void; now: Date }> = ({
  label, items, activePath, onLink, now,
}) => (
  <div className="mb-6">
    <p className="eyebrow-v2 px-3 mb-2">{label}</p>
    <nav className="flex flex-col gap-0.5">
      {items.map((it) => {
        const active = !it.external && (activePath === it.to || activePath.startsWith(it.to + "/"));
        const open = it.channel ? isChannelOpen(it.channel, now) : null;
        const cls = `flex items-center gap-3 h-[44px] px-3 rounded-xl text-sm font-medium transition-colors ${
          active
            ? "bg-ink-100 text-ink-1000"
            : "text-ink-700 hover:bg-ink-50 hover:text-ink-1000"
        }`;
        const inner = (
          <>
            {active && (
              <span
                className="absolute left-0 w-1 h-6 rounded-r-full"
                style={{ background: "var(--accent)" }}
              />
            )}
            <span className={active ? "text-ink-1000" : "text-ink-500"}>{it.icon}</span>
            <span className="flex-1 truncate">{it.label}</span>
            {it.badge && <NavBadge badge={it.badge} />}
            {it.external && (
              <span className="text-ink-400 text-[10px] font-bold" aria-hidden>↗</span>
            )}
            {open !== null && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 h-[18px] rounded-full text-[9px] font-bold uppercase tracking-wider ${
                  open ? "bg-emerald-50 text-emerald-700" : "bg-ink-100 text-ink-500"
                }`}
                title={open ? "Open now" : "Closed"}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-emerald-500" : "bg-ink-400"}`} />
                {open ? "Open" : "Closed"}
              </span>
            )}
          </>
        );
        return it.external ? (
          <a key={it.to} href={it.to} target="_blank" rel="noopener noreferrer" onClick={onLink} className={cls}>
            {inner}
          </a>
        ) : (
          <Link key={it.to} to={it.to} onClick={onLink} className={cls}>
            {inner}
          </Link>
        );
      })}
    </nav>
  </div>
);

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const user = useSelector((state: State) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [now, setNow] = useState(() => new Date());
  const [showChangePassword, setShowChangePassword] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);
  // iOS Safari overlays content with the dynamic URL bar — plain 100vh (and
  // even 100dvh on older WebKit) leaves the sidebar taller than the visible
  // area, so the Sign-out footer slides under the URL bar and is unreachable.
  // Measure the live visualViewport height and pin the sidebar to that.
  const [viewportH, setViewportH] = useState<number | null>(null);
  useEffect(() => {
    const update = () => {
      const vv = window.visualViewport;
      setViewportH(vv ? vv.height : window.innerHeight);
    };
    update();
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);
  const badges = useSidebarBadges();
  const { avatar } = useMyAvatar(user?.role ? user.id : null);
  const withBadges = (items: NavItem[]): NavItem[] =>
    items.map((it) => {
      if (it.to === "/ordering" && badges.incomingOrders > 0) {
        return { ...it, badge: { kind: "count", value: badges.incomingOrders } };
      }
      if (it.to === "/statusOrder" && badges.myReady > 0) {
        return { ...it, badge: { kind: "dot", value: badges.myReady } };
      }
      return it;
    });

  const role = user?.role;
  const showStaff = isStaff(role);
  const showAdmin = isAdminLike(role);
  const showDev = role === "dev";
  const showUser = !!role; // anyone signed in can preview user pages

  const handleLogout = () => {
    void logoutSession();
    resetMyAvatarStore();
    dispatch({ type: "LOGOUT" });
    navigate("/login", { replace: true });
  };

  // close on route change (mobile)
  useEffect(() => {
    onClose();
    // Route-change only: onClose is an unstable parent callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/* Backdrop — shown at all sizes whenever the sidebar is open, so the
          tablet/desktop overlay can be dismissed by clicking outside too. */}
      {open && (
        <div
          className="fixed inset-0 bg-ink-1000/40 z-40"
          onClick={onClose}
        />
      )}

      <aside
        style={viewportH ? { height: `${viewportH}px` } : undefined}
        className={`cafe-sidebar fixed top-0 left-0 w-[280px] z-50 bg-white border-r border-ink-100 flex flex-col font-sans transition-transform duration-200 ${
          viewportH ? "" : "h-screen"
        } ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand + close (mobile) */}
        <div className="px-5 pt-[20px] pb-4 flex items-center justify-between border-b border-ink-100">
          <img src={wordmark} alt="Woxa Cafe" className="h-[28px] w-auto" />
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-ink-700 hover:bg-ink-100"
            aria-label="Close menu"
          >
            <Icon>{I.close}</Icon>
          </button>
        </div>

        {/* User card */}
        {role && (
          <div className="px-5 py-4 border-b border-ink-100">
            <div className="flex items-center gap-3">
              <UserAvatar avatar={avatar} name={user?.name} size={40} />
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-sm text-ink-1000 truncate">
                  {user?.name ?? "Signed in"}
                </p>
                <p className="text-xs text-ink-500 truncate">{user?.email}</p>
              </div>
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 h-[24px] rounded-full bg-ink-100 text-[11px] font-bold text-ink-900">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--accent)" }}
              />
              {ROLE_LABEL[role] ?? role}
            </div>
          </div>
        )}

        {/* Nav sections — scrollable */}
        <div className="flex-1 overflow-y-auto px-2 py-4">
          {showUser && (
            <Section label="For everyone" items={withBadges(userNav)} activePath={location.pathname} onLink={onClose} now={now} />
          )}
          {showStaff && (
            <Section label="Barista" items={withBadges(baristaNav)} activePath={location.pathname} onLink={onClose} now={now} />
          )}
          {showAdmin && (
            <Section label="Admin" items={adminNav} activePath={location.pathname} onLink={onClose} now={now} />
          )}
          {showDev && (
            <Section label="Developer" items={devNav} activePath={location.pathname} onLink={onClose} now={now} />
          )}
        </div>

        {/* Footer — change password & logout */}
        <div className="border-t border-ink-100 px-2 py-3 space-y-1">
          {role && (
            <Link
              to={showAdmin ? "/profile/avatar-admin" : "/profile/avatar"}
              onClick={onClose}
              className="w-full flex items-center gap-3 h-[44px] px-3 rounded-xl text-sm font-medium text-ink-700 hover:bg-ink-50 hover:text-ink-1000"
            >
              <Icon>{I.face}</Icon>
              <span className="flex-1 text-left">แต่งรูปโปรไฟล์</span>
            </Link>
          )}
          <button
            type="button"
            onClick={() => setShowChangePassword(true)}
            className="w-full flex items-center gap-3 h-[44px] px-3 rounded-xl text-sm font-medium text-ink-700 hover:bg-ink-50 hover:text-ink-1000"
          >
            <Icon>{I.lock}</Icon>
            <span className="flex-1 text-left">Change password</span>
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 h-[44px] px-3 rounded-xl text-sm font-medium text-ink-700 hover:bg-ink-50 hover:text-ink-1000"
          >
            <Icon>{I.logout}</Icon>
            <span className="flex-1 text-left">Sign out</span>
          </button>
        </div>
      </aside>

      <ChangePasswordModal isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </>
  );
};

// Top bar with the hamburger + small brand mark. Shown at every size now
// that the sidebar is collapsible on tablet/desktop too — the user needs a
// way back in. Sits inline (not fixed) so content flows below it.
export const MobileTopbar: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <div className="cafe-topbar sticky top-0 z-30 h-[56px] bg-white/95 backdrop-blur-md border-b border-ink-100 flex items-center px-3">
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open menu"
      className="w-[40px] h-[40px] rounded-full hover:bg-ink-100 flex items-center justify-center text-ink-1000"
    >
      <Icon>{I.menu}</Icon>
    </button>
    <div className="flex items-center ml-1">
      <img src={wordmark} alt="Woxa Cafe" className="h-[22px] w-auto" />
      <HalloweenArt variant="witch-hat" className="art-nav" />
    </div>
  </div>
);

// keep old name for compat (unused now)
export const SidebarToggle = MobileTopbar;

export default Sidebar;
